import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    validation: Boolean,
  };

  static targets = ["birthdate", "userGuardianFields", "cpf"];

  // CONNECT: Roda assim que o componente renderiza na tela
  connect() {
    // Armazena os campos originais do responsável de forma segura
    this.guardianFields = this.hasUserGuardianFieldsTarget
      ? Array.from(
          this.userGuardianFieldsTarget.querySelectorAll(".user-guardian-field")
        )
      : [];

    // Executa a validação inicial (ajusta botão, mensagens e responsável no render)
    this.validate();
  }

  // VALIDATIONS PRINCIPAIS
  validate() {
    this.validateBirthdate();

    if (this.validationValue) {
      this.validateVisual();
    }
    this.toggleResponsibleFields();
  }

  validateVisual() {
    // Adicione outras validações visuais aqui se necessário no futuro
  }

  validateBirthdate() {
    const rawValue = this.birthdateTarget.value;
    const birthdate = rawValue.substring(0, 10);
    const submitButton = document.getElementById("candidate-form-submit");

    if (rawValue.length === 0) {
      this.toggleInvalidClass(this.birthdateTarget, false);
      this.removeButtonErrorMessage();
      if (submitButton) {
        submitButton.disabled = false;
      }
      return;
    }

    if (birthdate.length < 10) {
      this.toggleInvalidClass(this.birthdateTarget, true);
      if (submitButton) {
        submitButton.disabled = true;
        this.showButtonErrorMessage(
          submitButton,
          "Insira uma data de nascimento válida. ex: 14/03/2001"
        );
      }
      return;
    }

    const isDateValid = this.isValidDate(birthdate);

    this.toggleInvalidClass(this.birthdateTarget, !isDateValid);

    if (submitButton) {
      submitButton.disabled = !isDateValid;

      if (!isDateValid) {
        this.showButtonErrorMessage(
          submitButton,
          "A data de nascimento informada é inválida."
        );
      } else {
        this.removeButtonErrorMessage();
      }
    }
  }

  validateCPF() {
    if (!this.hasCpfTarget) return;

    const cpf = this.cpfTarget.value.replace(/\D/g, "");

    if (cpf.length !== 11 || !cpf.length) {
      this.toggleInvalidClass(this.cpfTarget, true);
      return;
    }

    const valid = this.isValidCPF(cpf);
    this.toggleInvalidClass(this.cpfTarget, !valid);
  }

  // VERIFICAÇÕES DE CALENDÁRIO E DOCUMENTO
  isValidDate(dateString) {
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!regex.test(dateString)) return false;

    const [day, month, year] = dateString.split("/").map(Number);

    if (year < 1900 || year > new Date().getFullYear()) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;

    // Impede datas impossíveis como 31/04 ou 30/02
    const date = new Date(year, month - 1, day);
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  }

  isValidCPF(cpf) {
    if (/^(\d)\1+$/.test(cpf)) return false;

    let sum = 0;
    let remainder;

    for (let i = 1; i <= 9; i++) {
      sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
    }

    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpf.substring(9, 10))) return false;

    sum = 0;
    for (let i = 1; i <= 10; i++) {
      sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
    }

    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;

    return remainder === parseInt(cpf.substring(10, 11));
  }

  // GERENCIAMENTO DOS CAMPOS DE RESPONSÁVEL (MENOR DE IDADE)
  toggleResponsibleFields() {
    const birthdate = this.birthdateTarget.value;

    if (!this.isValidDate(birthdate)) {
      this.hide();
      return;
    }

    const age = this.calculateAge(birthdate);

    if (age < 18) {
      this.show();
    } else {
      this.hide();
    }
  }

  calculateAge(dateString) {
    const [day, month, year] = dateString.split("/").map(Number);
    const birth = new Date(year, month - 1, day);
    const today = new Date();

    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }

    return age;
  }

  toggleUserGuardianFields({ show }) {
    if (!this.hasUserGuardianFieldsTarget) return;

    if (show) {
      this.guardianFields.forEach((el) => {
        el.querySelectorAll("input, select, textarea").forEach((input) => {
          input.disabled = false;
        });
        this.userGuardianFieldsTarget.appendChild(el);
      });
      return;
    }

    this.guardianFields.forEach((el) => {
      el.querySelectorAll("input, select, textarea").forEach((input) => {
        input.value = "";
        input.disabled = true;
        input.classList.remove("is-invalid", "is-valid");
      });
      el.remove();
    });
  }

  show() {
    if (!this.hasUserGuardianFieldsTarget) return;
    this.userGuardianFieldsTarget.classList.remove("d-none");
    this.toggleUserGuardianFields({ show: true });
  }

  hide() {
    if (!this.hasUserGuardianFieldsTarget) return;
    this.userGuardianFieldsTarget.classList.add("d-none");
    this.toggleUserGuardianFields({ show: false });
  }

  // HELPERS DE INTERFACE (CLASSES E ALERTAS DINÂMICOS)
  toggleInvalidClass(element, invalid) {
    if (invalid) {
      element.classList.add("is-invalid");
    } else {
      element.classList.remove("is-invalid");
    }
  }

  showButtonErrorMessage(buttonElement, message) {
    if (document.getElementById("birthdate-submit-error")) return;

    const errorDiv = document.createElement("div");
    errorDiv.id = "birthdate-submit-error";
    errorDiv.className = "mb-2 p-2 d-flex align-items-center gap-2 text-danger";

    // Estilização customizada (fundo vermelho leve e translúcido, cantos arredondados)
    errorDiv.style.backgroundColor = "rgba(220, 53, 69, 0.1)";
    errorDiv.style.border = "1px solid rgba(220, 53, 69, 0.3)";
    errorDiv.style.borderRadius = "0.375rem";
    errorDiv.style.fontSize = "0.875rem";

    // Estrutura interna com ícone do Font Awesome
    errorDiv.innerHTML = `
      <i class="fa-solid fa-circle-exclamation"></i>
      <span>${message}</span>
    `;

    // Posiciona dinamicamente colado logo acima do botão
    buttonElement.parentNode.insertBefore(errorDiv, buttonElement);
  }

  removeButtonErrorMessage() {
    const existingError = document.getElementById("birthdate-submit-error");
    if (existingError) {
      existingError.remove();
    }
  }
}
